import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  Shield,
  BookOpen,
  LogOut,
  ChevronRight,
  MessageCircle,
  Key,
  CreditCard,
  Edit3,
  Check,
  X,
  ExternalLink,
} from 'lucide-react';
import { useAuth, DiscordLink } from '../components/auth';
import * as apiClient from '../lib/api';

type Tab = 'profile' | 'security' | 'billing';

export default function Account() {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(tabParam || 'profile');

  // Update tab when URL param changes
  useEffect(() => {
    if (tabParam && ['profile', 'security', 'billing'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleLogout = async () => {
    await logout();
  };

  const navItems = [
    { id: 'profile' as Tab, label: 'Profile', icon: User },
    { id: 'security' as Tab, label: 'Security', icon: Shield },
    { id: 'billing' as Tab, label: 'Billing', icon: CreditCard },
  ];

  return (
    <div className="account-page-full">
      {/* Header */}
      <header className="account-top-header">
        <div className="account-top-container">
          <Link to="/" className="account-logo">
            Fasting <span>Carnivore</span>
          </Link>
          <nav className="account-top-nav">
            <Link to="/dashboard" className="account-nav-link">
              <BookOpen size={18} />
              My Library
            </Link>
            <button onClick={handleLogout} className="account-nav-link account-logout">
              <LogOut size={18} />
              Sign Out
            </button>
          </nav>
        </div>
      </header>

      <div className="account-layout">
        {/* Sidebar */}
        <aside className="account-sidebar">
          <div className="account-user-card">
            <div className="account-avatar-lg">
              {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="account-user-info">
              <h2>{user?.name || 'Member'}</h2>
              <p>{user?.email}</p>
            </div>
          </div>

          <nav className="account-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-tab={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`account-nav-item ${activeTab === item.id ? 'active' : ''}`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
                <ChevronRight size={16} className="account-nav-arrow" />
              </button>
            ))}
          </nav>

          <div className="account-sidebar-footer">
            <Link to="/dashboard" className="account-back-btn">
              <BookOpen size={18} />
              Back to Library
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="account-main">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'profile' && <ProfileSection user={user} />}
            {activeTab === 'security' && <SecuritySection />}
            {activeTab === 'billing' && <BillingSection user={user} />}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

function ProfileSection({ user }: { user: any }) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    setSaving(true);
    setSaveError('');
    try {
      await apiClient.auth.updateProfile({ name: trimmed });
      setIsEditingName(false);
      // Reload page to refresh user data from AuthProvider
      window.location.reload();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(user?.name || '');
    setIsEditingName(false);
    setSaveError('');
  };

  return (
    <div className="account-section-full">
      <div className="account-section-header-full">
        <User size={24} />
        <div>
          <h1>Profile</h1>
          <p>Manage your personal information</p>
        </div>
      </div>

      <div className="account-card-full">
        <div className="account-field">
          <label>Name</label>
          {isEditingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="login-input"
                style={{ flex: 1, margin: 0 }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
              />
              <button
                onClick={handleSaveName}
                disabled={saving || !editName.trim()}
                style={{
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                <Check size={16} />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                style={{
                  background: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="account-field-value">{user?.name || 'Not set'}</div>
              <button
                onClick={() => {
                  setEditName(user?.name || '');
                  setIsEditingName(true);
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  color: '#666',
                }}
              >
                <Edit3 size={14} />
                Edit
              </button>
            </div>
          )}
          {saveError && (
            <p style={{ color: 'var(--accent)', fontSize: '13px', marginTop: '4px' }}>{saveError}</p>
          )}
        </div>
        <div className="account-field">
          <label>Email</label>
          <div className="account-field-value">{user?.email}</div>
        </div>
        <div className="account-field">
          <label>Status</label>
          <div className="account-field-value">{user?.hasPurchased ? 'Lifetime Member' : 'Free Account'}</div>
        </div>
      </div>
    </div>
  );
}

function SecuritySection() {
  return (
    <div className="account-section-full">
      <div className="account-section-header-full">
        <Shield size={24} />
        <div>
          <h1>Security</h1>
          <p>Your account security settings</p>
        </div>
      </div>

      {/* Login Method */}
      <div className="account-card-full">
        <div className="account-card-header">
          <Key size={20} />
          <div>
            <h3>Sign-In Method</h3>
            <p>You sign in using magic links sent to your email</p>
          </div>
        </div>
        <div className="account-status-row">
          <span className="account-status-badge success">Active</span>
          <span className="account-status-text">Magic link authentication enabled</span>
        </div>
      </div>

      {/* Discord Recovery Section */}
      <div className="account-card-full">
        <div className="account-card-header">
          <MessageCircle size={20} />
          <div>
            <h3>Discord Recovery</h3>
            <p>Backup access if you lose your device</p>
          </div>
        </div>
        <DiscordLink />
      </div>
    </div>
  );
}

function BillingSection({ user }: { user: any }) {
  return (
    <div className="account-section-full">
      <div className="account-section-header-full">
        <CreditCard size={24} />
        <div>
          <h1>Billing</h1>
          <p>Manage your membership and payment methods</p>
        </div>
      </div>

      {/* Membership Status */}
      <div className="account-card-full">
        <div className="account-card-header">
          <CreditCard size={20} />
          <div>
            <h3>Membership Status</h3>
            <p>Your current plan and billing information</p>
          </div>
        </div>
        <div className="account-status-row">
          <span className={`account-status-badge ${user?.hasPurchased ? 'success' : 'warning'}`}>
            {user?.hasPurchased ? 'Lifetime Member' : 'Free Account'}
          </span>
          <span className="account-status-text">
            {user?.hasPurchased ? 'Full access to all content' : 'Upgrade to access premium content'}
          </span>
        </div>
      </div>

      {/* Billing Management */}
      {user?.hasPurchased && (
        <div className="account-card-full">
          <div className="account-card-header">
            <CreditCard size={20} />
            <div>
              <h3>Billing Management</h3>
              <p>View your membership details and manage billing</p>
            </div>
          </div>

          <p className="account-billing-text">
            Your lifetime membership is active. No recurring charges.
          </p>
        </div>
      )}

      {/* Upgrade CTA for non-members */}
      {!user?.hasPurchased && (
        <div className="account-card-full">
          <div className="account-upgrade-cta">
            <h3>Unlock Full Access</h3>
            <p>Get lifetime access to both books, bonus materials, and our private community.</p>
            <Link to="/join" className="btn btn-primary">
              Get Started — $97 Lifetime
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

